import { colorTokens, KariusButton, KariusLink, KariusTextField } from '@kariusdx/design-system';
import { Box, CircularProgress, Grid, Modal, Stack, Typography } from '@mui/material';
import React, { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { EngagementEvents, logKariusEvent } from '../Analytics';

interface PasswordResetModalProps {
  open: boolean;
  resetEmail: string | null;
  handleClose: () => void;
}

const style = {
  position: 'absolute',
  top: '35%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 410,
  minHeight: 286,
  backgroundColor: 'background.paper',
  boxShadow: 24,
  p: '16px',
  borderRadius: '3.5px',
};

export const PasswordResetModal: React.FC<PropsWithChildren<PasswordResetModalProps>> = ({
  open,
  resetEmail,
  handleClose,
}) => {
  const [textInput, setTextInput] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const hasPrefilled = useRef(false);
  useEffect(() => {
    if (!open) {
      hasPrefilled.current = false;
      return;
    }
    if (resetEmail && !hasPrefilled.current) {
      setTextInput(resetEmail);
      hasPrefilled.current = true;
    }
  }, [open, resetEmail]);

  const modalTitle = useMemo(() => {
    return emailSent ? 'Reset password email sent' : 'Reset password';
  }, [emailSent]);

  const handleSubmit = async () => {
    logKariusEvent(EngagementEvents.reset_password_submit_tapped, {
      modal_title: modalTitle,
      email_or_username: textInput,
    });
    setErrorMessage('');
    setLoading(true);
    const networkErrorMessage =
      'Failed to reset password. Please contact Customer Success at (866) 452-7487 or help@kariusdx.com';
    try {
      const res = await fetch('/api/auth0/passwordReset', {
        method: 'POST',
        body: JSON.stringify({
          text: textInput,
          modalTitle,
        }),
      });
      if (res.ok) {
        setEmailSent(true);
      } else {
        setErrorMessage(networkErrorMessage);
      }
    } catch (_e) {
      setErrorMessage(networkErrorMessage);
    } finally {
      setLoading(false);
    }
  };
  const closeModal = () => {
    if (!emailSent) {
      logKariusEvent(EngagementEvents.reset_password_cancel_tapped, {
        modal_title: modalTitle,
      });
    }
    setTextInput('');
    setEmailSent(false);
    setErrorMessage('');
    handleClose();
  };
  return (
    <Modal
      open={open}
      onClose={closeModal}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Box sx={style}>
        <Typography id='modal-modal-title' variant='heading_02'>
          {modalTitle}
        </Typography>
        <Grid id='modal-modal-description' container>
          <Box
            sx={{
              mt: '24px',
            }}
          >
            {!emailSent && (
              <Stack spacing={'20px'}>
                <Typography variant='body_01' sx={{ color: colorTokens.text_02 }}>
                  Enter the email address or username for your account and an email will be sent that enables you to
                  create a new password
                </Typography>
                <KariusTextField
                  id='email'
                  name='email'
                  label='Email or username*'
                  value={textInput}
                  error={!!errorMessage}
                  helperText={errorMessage}
                  onChange={(e) => setTextInput(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    height: 50,
                    '& .MuiInputBase-root': {
                      height: 50,
                    },
                  }}
                />
              </Stack>
            )}

            {emailSent && (
              <Typography
                variant='body_02'
                sx={{
                  fontSize: '14px',
                  lineHeight: '20px',
                  marginTop: '20px',
                  letterSpacing: '0.25px',
                  color: colorTokens.text_02,
                }}
              >
                If you don’t see the email in your inbox, please check your spam folder.
                <br />
                <br />
                <>
                  If you need assistance, please contact Karius at <br />
                  <KariusLink className='link-text' underline href={'tel:8664527487'}>
                    (866) 452-7487
                  </KariusLink>{' '}
                  or{' '}
                  <KariusLink href={'mailto:help@kariusdx.com'} underline className='link-text'>
                    help@kariusdx.com
                  </KariusLink>
                  .
                </>
              </Typography>
            )}
          </Box>
          <Grid
            container
            item
            justifyContent='flex-end'
            xs={12}
            mt='42px'
            gap='12px'
            sx={{
              textAlign: 'right',
            }}
          >
            <KariusButton
              variant={emailSent ? `secondary` : `tertiary`}
              rounded
              sx={{
                width: emailSent ? '110px' : 'auto',
              }}
              onClick={closeModal}
            >
              {emailSent ? 'Close' : 'Cancel'}
            </KariusButton>
            {!emailSent && (
              <KariusButton
                variant='primary'
                rounded
                disabled={!textInput || loading}
                sx={{
                  minWidth: '110px',
                  color: !textInput || loading ? colorTokens.ui_02 : colorTokens.text_05,
                }}
                onClick={handleSubmit}
              >
                {loading && <CircularProgress size={13} sx={{ color: colorTokens.text_02, marginRight: '5px' }} />}
                Submit
              </KariusButton>
            )}
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};
